import React, { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { OdrLoadingSpinner } from '@/components/ui/spinner';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/utils/policies';

const COURT_CALENDAR_COURT_HOUSES = gql`
  query CourtCalendarCourtHouses {
    courtCalendarCourtHouses {
      id
      name
      defaultDepartmentId
      defaultDepartmentName
    }
  }
`;

const COURT_CALENDAR_DEPARTMENTS = gql`
  query CourtCalendarDepartments($courtIds: [Int]) {
    courtCalendarDepartments(courtIds: $courtIds) {
      courtId
      id
      name
    }
  }
`;

const ALL_PROGRAM_OPTIONS = [
  'MIST',
  'FIST',
  'ODR Housing',
  'Pregnant Woman',
  'DSH Diversion',
];

const DEFAULT_PROGRAMS = [
  'ODR Housing',
  'Pregnant Woman',
  'DSH Diversion',
];

const DEFAULT_HEARING_TYPES = [
  'Diversion Hearing',
  'Setting Of Violation Hearing',
  'Progress Report',
  'Further Proceedings',
  'Diversion Violation Hearing',
];

const formatLosAngelesDateInput = (value: Date) => new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Los_Angeles',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
}).format(value);

const createDefaultDateRange = () => {
  const hearingDateFrom = new Date();
  const hearingDateTo = new Date();
  hearingDateTo.setDate(hearingDateTo.getDate() + 60);

  return {
    hearingDateFrom: formatLosAngelesDateInput(hearingDateFrom),
    hearingDateTo: formatLosAngelesDateInput(hearingDateTo),
  };
};

const DEFAULT_DATE_RANGE = createDefaultDateRange();

type CourtCalendarCourtHouse = {
  id: number;
  name: string;
  defaultDepartmentId?: number | null;
  defaultDepartmentName?: string | null;
};

type CourtCalendarDepartment = {
  courtId: number;
  id: number;
  name: string;
};

type CourtCalendarCourtHousesData = {
  courtCalendarCourtHouses: CourtCalendarCourtHouse[];
};

type CourtCalendarDepartmentsData = {
  courtCalendarDepartments: CourtCalendarDepartment[];
};

type CourtLocationRow = {
  key: string;
  courthouse: string;
  department: string;
};

const hasCourtCalendarAccess = (user: any) => {
  return hasAnyPolicy(user, ['tools:courtcalendar~full', 'admin:access~full']);
};

export const CourtCalendar = () => {
  const { user, isUserLoading } = useUser();
  const canAccess = hasCourtCalendarAccess(user);

  const [selectedPrograms, setSelectedPrograms] = useState<string[]>(DEFAULT_PROGRAMS);
  const [locationRows, setLocationRows] = useState<CourtLocationRow[]>([]);
  const [nextLocationRowId, setNextLocationRowId] = useState(0);
  const [hasInitializedDefaults, setHasInitializedDefaults] = useState(false);
  const [hearingDateFrom, setHearingDateFrom] = useState(DEFAULT_DATE_RANGE.hearingDateFrom);
  const [hearingDateTo, setHearingDateTo] = useState(DEFAULT_DATE_RANGE.hearingDateTo);
  const [hearingTypes, setHearingTypes] = useState<string[]>(DEFAULT_HEARING_TYPES);
  const [hearingTypeInput, setHearingTypeInput] = useState('');

  const {
    data: courtHousesData,
    loading: courtHousesLoading,
    error: courtHousesError,
  } = useQuery<CourtCalendarCourtHousesData>(COURT_CALENDAR_COURT_HOUSES, {
    fetchPolicy: 'cache-and-network',
    skip: !canAccess,
  });

  const selectedCourtIds = Array.from(new Set(
    locationRows
      .map((row) => Number(row.courthouse))
      .filter((value) => Number.isInteger(value) && value > 0)
  ));

  const {
    data: departmentsData,
    loading: departmentsLoading,
    error: departmentsError,
  } = useQuery<CourtCalendarDepartmentsData>(COURT_CALENDAR_DEPARTMENTS, {
    fetchPolicy: 'cache-and-network',
    skip: !canAccess || selectedCourtIds.length === 0,
    variables: {
      courtIds: selectedCourtIds,
    },
  });

  const courtHouses = courtHousesData?.courtCalendarCourtHouses ?? [];
  const departments = departmentsData?.courtCalendarDepartments ?? [];

  useEffect(() => {
    if (courtHouses.length === 0) {
      return;
    }

    if (hasInitializedDefaults) {
      return;
    }

    setLocationRows(courtHouses.map((courtHouse, index) => ({
      key: `legacy-default-${index}`,
      courthouse: `${courtHouse.id}`,
      department: courtHouse.defaultDepartmentId ? `${courtHouse.defaultDepartmentId}` : '',
    })));
    setNextLocationRowId(courtHouses.length);
    setHasInitializedDefaults(true);
  }, [courtHouses, hasInitializedDefaults]);

  useEffect(() => {
    if (departments.length === 0) {
      return;
    }

    setLocationRows((current) => {
      let changed = false;
      const nextRows = current.map((row) => {
        const departmentOptions = departments.filter((department) => `${department.courtId}` === row.courthouse);
        if (departmentOptions.length === 0) {
          return row;
        }

        if (departmentOptions.some((department) => `${department.id}` === row.department)) {
          return row;
        }

        changed = true;
        return {
          ...row,
          department: `${departmentOptions[0].id}`,
        };
      });

      if (!changed) {
        return current;
      }

      return nextRows;
    });
  }, [departments]);

  const getDepartmentOptions = (courthouse: string) => departments.filter((department) => `${department.courtId}` === courthouse);

  const toggleProgram = (program: string) => {
    if (selectedPrograms.includes(program)) {
      setSelectedPrograms((current) => current.filter((item) => item !== program));
      return;
    }

    setSelectedPrograms((current) => [...current, program]);
  };

  const addLocationRow = () => {
    if (courtHouses.length === 0) {
      return;
    }

    const defaultCourtHouse = courtHouses[0];
    const defaultDepartmentOptions = getDepartmentOptions(`${defaultCourtHouse.id}`);
    const defaultDepartment = defaultDepartmentOptions[0]
      ? `${defaultDepartmentOptions[0].id}`
      : defaultCourtHouse.defaultDepartmentId
        ? `${defaultCourtHouse.defaultDepartmentId}`
        : '';

    setLocationRows((current) => [
      ...current,
      {
        key: `location-row-${nextLocationRowId}`,
        courthouse: `${defaultCourtHouse.id}`,
        department: defaultDepartment,
      },
    ]);
    setNextLocationRowId((current) => current + 1);
  };

  const removeLocationRow = (rowKey: string) => {
    setLocationRows((current) => current.filter((row) => row.key !== rowKey));
  };

  const updateLocationCourtHouse = (rowKey: string, courthouse: string) => {
    const departmentOptions = getDepartmentOptions(courthouse);
    const nextDepartment = departmentOptions[0] ? `${departmentOptions[0].id}` : '';

    setLocationRows((current) => current.map((row) => {
      if (row.key !== rowKey) {
        return row;
      }

      return {
        ...row,
        courthouse,
        department: nextDepartment,
      };
    }));
  };

  const updateLocationDepartment = (rowKey: string, department: string) => {
    setLocationRows((current) => current.map((row) => {
      if (row.key !== rowKey) {
        return row;
      }

      return {
        ...row,
        department,
      };
    }));
  };

  const addHearingType = () => {
    const normalized = hearingTypeInput.trim();
    if (!normalized) {
      return;
    }

    if (hearingTypes.some((item) => item.toLowerCase() === normalized.toLowerCase())) {
      setHearingTypeInput('');
      return;
    }

    setHearingTypes((current) => [...current, normalized]);
    setHearingTypeInput('');
  };

  const removeHearingType = (hearingType: string) => {
    setHearingTypes((current) => current.filter((item) => item !== hearingType));
  };

  if (isUserLoading) {
    return <OdrLoadingSpinner message="Loading Court Calendar" />;
  }

  if (!canAccess) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Court Calendar</h1>
        <p className="mt-2 text-sm text-gray-600">You do not have Court Calendar access.</p>
      </div>
    );
  }

  if (courtHousesLoading && !courtHousesData) {
    return <OdrLoadingSpinner message="Loading Court Calendar metadata" />;
  }

  if (courtHousesError) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Court Calendar</h1>
        <p className="mt-2 text-sm text-red-700">{courtHousesError.message}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader className="gap-3">
          <div>
            <CardTitle>Court Calendar</CardTitle>
            <CardDescription>
              Metadata and filter layer for the LA Court calendar search. Search results and export stay in the next ticket.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Programs</h2>
              <p className="text-sm text-gray-600">Legacy defaults stay selected until you change them.</p>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
              {ALL_PROGRAM_OPTIONS.map((program) => (
                <label
                  key={program}
                  className="flex items-center gap-2 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] px-3 py-2 text-sm"
                >
                  <input
                    type="checkbox"
                    checked={selectedPrograms.includes(program)}
                    onChange={() => toggleProgram(program)}
                  />
                  <span>{program}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Hearing Locations</h2>
                <p className="text-sm text-gray-600">Court house and department selections come from Nova metadata.</p>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addLocationRow} disabled={courtHouses.length === 0}>
                <Plus className="mr-2 h-4 w-4" />
                Add Location
              </Button>
            </div>

            <div className="space-y-3">
              {locationRows.map((row) => {
                const departmentOptions = getDepartmentOptions(row.courthouse);
                return (
                  <div key={row.key} className="grid gap-3 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] p-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]">
                    <div className="space-y-2">
                      <Label>Court House</Label>
                      <select
                        className="h-10 w-full rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-3 text-sm"
                        value={row.courthouse}
                        onChange={(event) => updateLocationCourtHouse(row.key, event.target.value)}
                      >
                        {courtHouses.map((courtHouse) => (
                          <option key={courtHouse.id} value={courtHouse.id}>
                            {courtHouse.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="space-y-2">
                      <Label>Department</Label>
                      <select
                        className="h-10 w-full rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-3 text-sm"
                        value={row.department}
                        onChange={(event) => updateLocationDepartment(row.key, event.target.value)}
                        disabled={departmentOptions.length === 0}
                      >
                        {departmentOptions.length === 0 && (
                          <option value="">No departments loaded</option>
                        )}
                        {departmentOptions.map((department) => (
                          <option key={`${department.courtId}-${department.id}`} value={department.id}>
                            {department.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="flex items-end">
                      <Button type="button" variant="outline" size="sm" onClick={() => removeLocationRow(row.key)}>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Remove
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>

            {departmentsLoading && (
              <p className="text-sm text-gray-600">Loading department metadata...</p>
            )}
            {departmentsError && (
              <p className="text-sm text-red-700">{departmentsError.message}</p>
            )}
          </section>

          <section className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="court-calendar-hearing-date-from">Hearing Date From</Label>
              <Input
                id="court-calendar-hearing-date-from"
                type="date"
                value={hearingDateFrom}
                onChange={(event) => setHearingDateFrom(event.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="court-calendar-hearing-date-to">Hearing Date To</Label>
              <Input
                id="court-calendar-hearing-date-to"
                type="date"
                value={hearingDateTo}
                onChange={(event) => setHearingDateTo(event.target.value)}
              />
            </div>
          </section>

          <section className="space-y-3">
            <div>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-[var(--neutral-gray-100)]">Hearing Types</h2>
              <p className="text-sm text-gray-600">Legacy defaults are loaded and can be edited as tags.</p>
            </div>

            <div className="flex flex-wrap gap-2">
              {hearingTypes.map((hearingType) => (
                <button
                  key={hearingType}
                  type="button"
                  className="rounded-full border border-[var(--neutral-gray-50)] bg-[var(--neutral-gray-10)] px-3 py-1 text-sm"
                  onClick={() => removeHearingType(hearingType)}
                >
                  {hearingType}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              <Input
                placeholder="Add hearing type"
                value={hearingTypeInput}
                onChange={(event) => setHearingTypeInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key !== 'Enter') {
                    return;
                  }

                  event.preventDefault();
                  addHearingType();
                }}
              />
              <Button type="button" variant="outline" onClick={addHearingType}>
                Add Hearing Type
              </Button>
            </div>
          </section>

          <div className="flex flex-wrap items-center justify-between gap-3 rounded-[var(--border-radius-medium)] border border-dashed border-[var(--neutral-gray-50)] bg-[var(--neutral-gray-10)] px-4 py-3">
            <div className="text-sm text-gray-600">
              Ticket 1 is live here: metadata, legacy defaults, and the filter form. Search/results/export stay in the next Court Calendar ticket.
            </div>
            <Button type="button" disabled>
              Search Coming Next
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
